import { haversineKm } from './MapUtils';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { getUserTimeZone, parseShipmentDayjs } from '@/common/utils/utils';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Pure logic for the "Needs attention" triage rail on the Home map.
 *
 * Ranks live shipments by operational urgency: critical alerts first, then
 * at-risk alerts, boosted by environmental exposure (inside an active hazard
 * polygon, or near an airport whose delays exceed the company threshold).
 */

export interface StreamShipment {
  shipment_id: string;
  reference_number?: string;
  latest_location?: {
    latest_lat?: number | string;
    latest_long?: number | string;
  };
  has_alert?: boolean;
  alert?: { is_critical?: boolean };
}

/**
 * A coordinate as a number, or NaN when it is missing. `Number(null)` and
 * `Number('')` are 0, which would put a shipment with no fix at [0, 0].
 */
export const toCoordinate = (value: unknown): number =>
  value === null || value === undefined || value === ''
    ? Number.NaN
    : Number(value);

/** The shipment's last known position, or null when it has none. */
export const shipmentPosition = (
  s: StreamShipment | null | undefined
): { lat: number; lng: number } | null => {
  const lat = toCoordinate(s?.latest_location?.latest_lat);
  const lng = toCoordinate(s?.latest_location?.latest_long);
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

/** A position for one shipment that the map's own data should use instead. */
export interface ShipmentPlace {
  shipmentId: string;
  lat: number;
  lng: number;
}

/**
 * The shipments with `place` applied, so its marker, circle and camera all
 * agree. The same array when there is nothing to change (no place, the
 * shipment is not in it, or it is already there).
 */
export const placeShipment = <T extends StreamShipment>(
  shipments: T[],
  place: ShipmentPlace | null | undefined
): T[] => {
  if (!place) return shipments;
  const index = shipments.findIndex(
    (s) => String(s.shipment_id) === place.shipmentId
  );
  if (index < 0) return shipments;
  const shipment = shipments[index];
  const current = shipmentPosition(shipment);
  if (current?.lat === place.lat && current?.lng === place.lng)
    return shipments;
  const placed = shipments.slice();
  placed[index] = {
    ...shipment,
    latest_location: {
      ...shipment.latest_location,
      latest_lat: place.lat,
      latest_long: place.lng,
    },
  };
  return placed;
};

interface HazardPolygonAlert {
  hazard_type?: string;
  geometry?: { lat: number; lng: number }[][];
}

export interface NewsFlashPolygonItem {
  polygons?: HazardPolygonAlert[];
}

export interface AirportMetaEntry {
  lat?: number | null;
  lng?: number | null;
  iata?: string;
  icao?: string;
  delayStats?: { delayed_cancelled_pct?: number };
}

export interface NeedsAttentionItem {
  shipmentId: string;
  referenceNumber: string;
  /**
   * 'arriving' and 'pickup' are /monitor-only: calm shipments filling free
   * slots (landing soon; due to be picked up soon).
   */
  severity: 'critical' | 'at_risk' | 'watch' | 'arriving' | 'pickup';
  reasons: string[];
  score: number;
  lat: number;
  lng: number;
}

const SCORE_CRITICAL = 100;
const SCORE_AT_RISK = 50;
const SCORE_IN_HAZARD = 30;
const SCORE_NEAR_DELAYED_AIRPORT = 20;
export const AIRPORT_EXPOSURE_RADIUS_KM = 75;
export const NEEDS_ATTENTION_MAX_ITEMS = 10;

/** Ray-casting point-in-polygon. Polygon is a ring of {lat, lng} points. */
export const isPointInPolygon = (
  lat: number,
  lng: number,
  ring: { lat: number; lng: number }[]
): boolean => {
  if (!Array.isArray(ring) || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const yi = ring[i].lat;
    const xi = ring[i].lng;
    const yj = ring[j].lat;
    const xj = ring[j].lng;
    const intersects =
      yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

const collectHazardRings = (
  newsFlashPolygon: NewsFlashPolygonItem[] | null | undefined
): { hazardType: string; ring: { lat: number; lng: number }[] }[] => {
  const rings: { hazardType: string; ring: { lat: number; lng: number }[] }[] =
    [];
  (newsFlashPolygon ?? []).forEach((item) => {
    (item?.polygons ?? []).forEach((alert) => {
      (alert?.geometry ?? []).forEach((ring) => {
        if (Array.isArray(ring) && ring.length >= 3) {
          rings.push({ hazardType: alert.hazard_type || 'Hazard', ring });
        }
      });
    });
  });
  return rings;
};

/**
 * Build the ranked triage list shown in the "Needs attention" rail.
 * Returns at most `maxItems` (default NEEDS_ATTENTION_MAX_ITEMS) items,
 * highest urgency first; /monitor asks for all of them to count and page.
 */
export const buildNeedsAttentionItems = ({
  shipments,
  newsFlashPolygon,
  airportMeta,
  delayThreshold,
  maxItems = NEEDS_ATTENTION_MAX_ITEMS,
}: {
  shipments: StreamShipment[] | null | undefined;
  newsFlashPolygon?: NewsFlashPolygonItem[] | null;
  airportMeta?: Record<string, AirportMetaEntry> | null;
  delayThreshold?: number;
  maxItems?: number;
}): NeedsAttentionItem[] => {
  const hazardRings = collectHazardRings(newsFlashPolygon);
  const delayedAirports = Object.values(airportMeta ?? {}).filter(
    (meta) =>
      typeof meta?.lat === 'number' &&
      typeof meta?.lng === 'number' &&
      (meta?.delayStats?.delayed_cancelled_pct || 0) > (delayThreshold ?? 0)
  );

  const items: NeedsAttentionItem[] = [];
  (shipments ?? []).forEach((s) => {
    const position = shipmentPosition(s);
    if (!position) return;
    const { lat, lng } = position;

    let score = 0;
    const reasons: string[] = [];
    const isCritical = Boolean(s?.alert?.is_critical);
    const hasAlert = Boolean(s?.has_alert);

    if (isCritical) {
      score += SCORE_CRITICAL;
      reasons.push('Critical alert');
    } else if (hasAlert) {
      score += SCORE_AT_RISK;
      reasons.push('At-risk alert');
    }

    const hazardHit = hazardRings.find(({ ring }) =>
      isPointInPolygon(lat, lng, ring)
    );
    if (hazardHit) {
      score += SCORE_IN_HAZARD;
      reasons.push(`In ${hazardHit.hazardType} area`);
    }

    let nearestAirport: AirportMetaEntry | null = null;
    let nearestKm = Infinity;
    delayedAirports.forEach((meta) => {
      const km = haversineKm(lat, lng, meta.lat as number, meta.lng as number);
      if (km <= AIRPORT_EXPOSURE_RADIUS_KM && km < nearestKm) {
        nearestKm = km;
        nearestAirport = meta;
      }
    });
    if (nearestAirport != null) {
      const airport = nearestAirport as AirportMetaEntry;
      score += SCORE_NEAR_DELAYED_AIRPORT;
      const code = airport.iata || airport.icao || 'airport';
      const pct = (airport.delayStats?.delayed_cancelled_pct || 0).toFixed(0);
      reasons.push(`Near ${code} · ${pct}% delays`);
    }

    if (score === 0) return;

    items.push({
      shipmentId: s.shipment_id,
      referenceNumber: s.reference_number || String(s.shipment_id),
      severity: isCritical ? 'critical' : hasAlert ? 'at_risk' : 'watch',
      reasons,
      score,
      lat,
      lng,
    });
  });

  items.sort((a, b) => b.score - a.score);
  return items.slice(0, maxItems);
};

/** "2d 3h", "4h 10m" or "25m" for a non-negative number of minutes. */
export const formatDurationMinutes = (totalMinutes: number): string => {
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  return days > 0
    ? `${days}d ${hours}h`
    : hours > 0
      ? `${hours}h ${minutes}m`
      : `${minutes}m`;
};

/**
 * Delayed airports (above `delayThreshold`) within
 * AIRPORT_EXPOSURE_RADIUS_KM of any of `shipments`: the only airports the
 * /monitor map shows, so each icon on the TV affects a shipment on screen.
 */
export const findNearbyDelayedAirports = (
  airportMeta: Record<string, AirportMetaEntry> | null | undefined,
  delayThreshold: number | undefined,
  shipments: StreamShipment[]
): string[] => {
  const points = shipments
    .map(shipmentPosition)
    .filter((p): p is { lat: number; lng: number } => p !== null)
    .map((p) => [p.lat, p.lng]);
  return Object.entries(airportMeta ?? {})
    .filter(
      ([, meta]) =>
        typeof meta?.lat === 'number' &&
        typeof meta?.lng === 'number' &&
        (meta?.delayStats?.delayed_cancelled_pct || 0) >
          (delayThreshold ?? 0) &&
        points.some(
          ([lat, lng]) =>
            haversineKm(lat, lng, meta.lat as number, meta.lng as number) <=
            AIRPORT_EXPOSURE_RADIUS_KM
        )
    )
    .map(([icao]) => icao)
    .sort();
};

/**
 * Time-to-act label for the shipment detail card.
 * Returns null when there is nothing actionable to show (already delivered,
 * or no usable target date).
 */
export const formatTimeToAct = (
  shipmentData: {
    actual_delivery_date?: string | null;
    estimated_delivery_date?: string | null;
    scheduled_delivery_date?: string | null;
  } | null,
  now: Date = new Date(),
  /** The user's profile timezone, which the detail's dates are written in. */
  timeZone: string = getUserTimeZone().name
): { label: string; urgency: 'ok' | 'soon' | 'overdue' } | null => {
  if (!shipmentData || shipmentData.actual_delivery_date) return null;
  const target =
    shipmentData.estimated_delivery_date ||
    shipmentData.scheduled_delivery_date;
  if (!target) return null;
  const parsed = parseShipmentDayjs(target);
  if (!parsed?.isValid()) return null;
  // The detail sends dates as text already in the profile timezone, with no
  // offset: read them there, not in the browser's timezone (a GMT+3 profile
  // on a US Central machine was 8 hours off). An explicit offset wins.
  // An offset only counts after a time ("12-10-2026" ends in "-2026").
  const hasOffset =
    /\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(
      String(target).trim()
    );
  let targetMs = parsed.valueOf();
  if (!hasOffset) {
    try {
      targetMs = dayjs
        .tz(parsed.format('YYYY-MM-DDTHH:mm:ss'), timeZone)
        .valueOf();
    } catch {
      // A profile timezone Intl rejects (getUserTimeZone passes unknown
      // values through) must not crash the card: keep the browser reading.
    }
  }
  if (!Number.isFinite(targetMs)) return null;

  const diffMinutes = Math.round((targetMs - now.getTime()) / 60000);
  const span = formatDurationMinutes(Math.abs(diffMinutes));

  if (diffMinutes < 0) {
    return { label: `Overdue by ${span}`, urgency: 'overdue' };
  }
  return {
    label: `Arrives in ${span}`,
    urgency: diffMinutes <= 360 ? 'soon' : 'ok',
  };
};
